import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import {
  cancelEvent,
  createEventMessage,
  deleteEvent,
  finishEvent,
  getApiErrorMessage,
  getEvent,
  listAllEventMedia,
  listAllEventMessages,
  listCounties,
  listCountries,
  listEvents,
  listEventTypes,
  listRegions,
  listSettlements,
  publishEvent,
  type EventStatus,
} from '../lib/api';
import { extractTextFromContentBlocks, isContentBlockMediaTitle, renderContentBlocksHtml } from '../lib/contentBlocks';
import { EVENTS_TEMPLATE } from '../templates/eventsTemplate';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const formatDateTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const formatOptionalDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const statusLabel = (status: EventStatus): string => {
  if (status === 'pending_review') return 'Pending Review';
  if (status === 'published') return 'Published';
  if (status === 'rejected') return 'Rejected';
  if (status === 'cancelled') return 'Cancelled';
  if (status === 'finished') return 'Finished';
  return 'Draft';
};

const statusBadgeClass = (status: EventStatus): string => {
  if (status === 'published') return 'bg-emerald-50 text-emerald-700';
  if (status === 'pending_review') return 'bg-amber-50 text-amber-700';
  if (status === 'rejected') return 'bg-rose-50 text-rose-700';
  if (status === 'cancelled') return 'bg-slate-200 text-slate-700';
  if (status === 'finished') return 'bg-indigo-50 text-indigo-700';
  return 'bg-slate-100 text-slate-700';
};

export default function EventsPage() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const template = useMemo(() => EVENTS_TEMPLATE, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const searchInput = root.querySelector<HTMLInputElement>('#events-search-input');
    const originFilter = root.querySelector<HTMLSelectElement>('#events-origin-filter');
    const statusFilter = root.querySelector<HTMLSelectElement>('#events-status-filter');
    const typeFilter = root.querySelector<HTMLSelectElement>('#events-type-filter');
    const orderingFilter = root.querySelector<HTMLSelectElement>('#events-ordering-filter');
    const resetFiltersBtn = root.querySelector<HTMLButtonElement>('#events-reset-filters-btn');
    const tbody = root.querySelector<HTMLTableSectionElement>('#events-table-body');
    const meta = root.querySelector<HTMLElement>('#events-table-meta');
    const prevBtn = root.querySelector<HTMLButtonElement>('#events-prev-btn');
    const nextBtn = root.querySelector<HTMLButtonElement>('#events-next-btn');
    const pageIndicator = root.querySelector<HTMLElement>('#events-page-indicator');
    const upcomingKpi = root.querySelector<HTMLElement>('#events-kpi-upcoming');
    const pendingKpi = root.querySelector<HTMLElement>('#events-kpi-pending');
    const publishedKpi = root.querySelector<HTMLElement>('#events-kpi-published');
    const finishedKpi = root.querySelector<HTMLElement>('#events-kpi-finished');

    const viewOverlay = root.querySelector<HTMLElement>('#events-view-overlay');
    const viewBackdrop = root.querySelector<HTMLElement>('#events-view-backdrop');
    const viewCloseBtn = root.querySelector<HTMLButtonElement>('#events-view-close-btn');
    const viewError = root.querySelector<HTMLElement>('#events-view-error');
    const viewTitle = root.querySelector<HTMLElement>('#events-view-title');
    const viewSubtitle = root.querySelector<HTMLElement>('#events-view-subtitle');
    const viewHero = root.querySelector<HTMLImageElement>('#events-view-hero');
    const viewGallery = root.querySelector<HTMLElement>('#events-view-gallery');
    const viewStatus = root.querySelector<HTMLElement>('#events-view-status');
    const viewOrigin = root.querySelector<HTMLElement>('#events-view-origin');
    const viewFeatured = root.querySelector<HTMLElement>('#events-view-featured');
    const viewType = root.querySelector<HTMLElement>('#events-view-type');
    const viewSlug = root.querySelector<HTMLElement>('#events-view-slug');
    const viewStarts = root.querySelector<HTMLElement>('#events-view-starts');
    const viewEnds = root.querySelector<HTMLElement>('#events-view-ends');
    const viewTimezone = root.querySelector<HTMLElement>('#events-view-timezone');
    const viewHost = root.querySelector<HTMLElement>('#events-view-host');
    const viewLocation = root.querySelector<HTMLElement>('#events-view-location');
    const viewCoordinates = root.querySelector<HTMLElement>('#events-view-coordinates');
    const viewRegion = root.querySelector<HTMLElement>('#events-view-region');
    const viewCounty = root.querySelector<HTMLElement>('#events-view-county');
    const viewSettlement = root.querySelector<HTMLElement>('#events-view-settlement');
    const viewGoing = root.querySelector<HTMLElement>('#events-view-going');
    const viewInterested = root.querySelector<HTMLElement>('#events-view-interested');
    const viewNotGoing = root.querySelector<HTMLElement>('#events-view-not-going');
    const viewCapacity = root.querySelector<HTMLElement>('#events-view-capacity');
    const viewPrice = root.querySelector<HTMLElement>('#events-view-price');
    const viewFree = root.querySelector<HTMLElement>('#events-view-free');
    const viewMeetingNote = root.querySelector<HTMLElement>('#events-view-meeting-note');
    const viewDescription = root.querySelector<HTMLElement>('#events-view-description');
    const viewModeration = root.querySelector<HTMLElement>('#events-view-moderation');
    const viewPublished = root.querySelector<HTMLElement>('#events-view-published');
    const viewCancelled = root.querySelector<HTMLElement>('#events-view-cancelled');
    const viewFinished = root.querySelector<HTMLElement>('#events-view-finished');
    const viewCreated = root.querySelector<HTMLElement>('#events-view-created');
    const viewUpdated = root.querySelector<HTMLElement>('#events-view-updated');
    const viewMessagesCount = root.querySelector<HTMLElement>('#events-view-messages-count');
    const viewMessages = root.querySelector<HTMLElement>('#events-view-messages');
    const viewMessageInput = root.querySelector<HTMLInputElement>('#events-view-message-input');
    const viewMessageSend = root.querySelector<HTMLButtonElement>('#events-view-message-send');
    const viewEditBtn = root.querySelector<HTMLButtonElement>('#events-view-edit-btn');
    const viewPublishBtn = root.querySelector<HTMLButtonElement>('#events-view-publish-btn');
    const viewCancelBtn = root.querySelector<HTMLButtonElement>('#events-view-cancel-btn');
    const viewFinishBtn = root.querySelector<HTMLButtonElement>('#events-view-finish-btn');
    const viewDeleteBtn = root.querySelector<HTMLButtonElement>('#events-view-delete-btn');

    if (
      !searchInput ||
      !originFilter ||
      !statusFilter ||
      !typeFilter ||
      !orderingFilter ||
      !resetFiltersBtn ||
      !tbody ||
      !meta ||
      !prevBtn ||
      !nextBtn ||
      !pageIndicator ||
      !upcomingKpi ||
      !pendingKpi ||
      !publishedKpi ||
      !finishedKpi ||
      !viewOverlay ||
      !viewBackdrop ||
      !viewCloseBtn ||
      !viewError ||
      !viewTitle ||
      !viewSubtitle ||
      !viewHero ||
      !viewGallery ||
      !viewStatus ||
      !viewOrigin ||
      !viewFeatured ||
      !viewType ||
      !viewSlug ||
      !viewStarts ||
      !viewEnds ||
      !viewTimezone ||
      !viewHost ||
      !viewLocation ||
      !viewCoordinates ||
      !viewRegion ||
      !viewCounty ||
      !viewSettlement ||
      !viewGoing ||
      !viewInterested ||
      !viewNotGoing ||
      !viewCapacity ||
      !viewPrice ||
      !viewFree ||
      !viewMeetingNote ||
      !viewDescription ||
      !viewModeration ||
      !viewPublished ||
      !viewCancelled ||
      !viewFinished ||
      !viewCreated ||
      !viewUpdated ||
      !viewMessagesCount ||
      !viewMessages ||
      !viewMessageInput ||
      !viewMessageSend ||
      !viewEditBtn ||
      !viewPublishBtn ||
      !viewCancelBtn ||
      !viewFinishBtn ||
      !viewDeleteBtn
    ) {
      return;
    }

    let disposed = false;
    let searchTimer: number | null = null;
    let currentPage = 1;
    const pageSize = 20;
    let openedEventId: number | null = null;
    let viewRequestId = 0;
    let countriesById = new Map<number, string>();
    let regionsById = new Map<number, string>();
    let countiesById = new Map<number, string>();
    let settlementsById = new Map<number, string>();
    let geoLoaded = false;

    const updateViewQueryParam = (value: string | null) => {
      const params = new URLSearchParams(window.location.search);
      if (value) params.set('view', value);
      else params.delete('view');
      const query = params.toString();
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    };

    const showViewError = (message: string) => {
      viewError.classList.remove('hidden');
      viewError.textContent = message;
    };

    const hideViewError = () => {
      viewError.classList.add('hidden');
      viewError.textContent = '';
    };

    const closeView = () => {
      openedEventId = null;
      viewRequestId += 1;
      viewOverlay.classList.add('hidden');
      updateViewQueryParam(null);
    };

    const setViewLoadingState = (id: number) => {
      hideViewError();
      viewTitle.textContent = 'Loading event...';
      viewSubtitle.textContent = `ID: #EVT-${id}`;
      viewStatus.textContent = 'Loading';
      viewOrigin.textContent = '-';
      viewFeatured.textContent = '-';
      viewType.textContent = '-';
      viewSlug.textContent = '-';
      viewStarts.textContent = '-';
      viewEnds.textContent = '-';
      viewTimezone.textContent = '-';
      viewHost.textContent = '-';
      viewLocation.textContent = '-';
      viewCoordinates.textContent = '-';
      viewRegion.textContent = '-';
      viewCounty.textContent = '-';
      viewSettlement.textContent = '-';
      viewGoing.textContent = '-';
      viewInterested.textContent = '-';
      viewNotGoing.textContent = '-';
      viewCapacity.textContent = '-';
      viewPrice.textContent = '-';
      viewFree.textContent = '-';
      viewMeetingNote.textContent = '-';
      viewModeration.textContent = '-';
      viewPublished.textContent = '-';
      viewCancelled.textContent = '-';
      viewFinished.textContent = '-';
      viewCreated.textContent = '-';
      viewUpdated.textContent = '-';
      viewHero.src =
        "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='675'><rect width='100%' height='100%' fill='%23e2e8f0'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%2364758b' font-size='30' font-family='Arial'>Loading</text></svg>";
      viewGallery.innerHTML = '';
      viewDescription.textContent = 'Loading...';
      viewMessages.innerHTML = '<p class="text-sm text-slate-500">Loading...</p>';
      viewMessagesCount.textContent = '-';
    };

    const ensureGeoLoaded = async () => {
      if (geoLoaded) return;
      const [countries, regions, counties, settlements] = await Promise.all([
        listCountries(),
        listRegions(),
        listCounties(),
        listSettlements(),
      ]);
      countriesById = new Map(countries.map((item) => [item.id, item.name]));
      regionsById = new Map(regions.map((item) => [item.id, item.name]));
      countiesById = new Map(counties.map((item) => [item.id, item.name]));
      settlementsById = new Map(settlements.map((item) => [item.id, item.name]));
      geoLoaded = true;
    };

    const renderRows = (items: Awaited<ReturnType<typeof listEvents>>['results']) => {
      if (!items.length) {
        tbody.innerHTML = '<tr><td class="px-4 py-8 text-slate-500 text-sm" colspan="9">No events found.</td></tr>';
        return;
      }

      tbody.innerHTML = items
        .map((item) => {
          const rsvpTotal = item.rsvp_going_count + item.rsvp_interested_count + item.rsvp_not_going_count;
          return `
            <tr class="hover:bg-slate-50/80 transition-colors ${openedEventId === item.id ? 'bg-primary/5' : ''}">
              <td class="px-4 py-4 text-xs font-mono text-slate-400">#EVT-${item.id}</td>
              <td class="px-4 py-4">
                <button data-event-action="view" data-event-id="${item.id}" class="font-bold text-slate-900 hover:text-primary text-left" type="button">${escapeHtml(item.title)}</button>
                <p class="text-xs text-slate-500">${escapeHtml(item.slug)}</p>
              </td>
              <td class="px-4 py-4">
                <span class="px-2 py-0.5 rounded text-[10px] font-bold uppercase ${item.origin_type === 'official' ? 'bg-blue-50 text-primary' : 'bg-amber-50 text-amber-700'}">${item.origin_type}</span>
              </td>
              <td class="px-4 py-4 text-slate-600">${escapeHtml(item.event_type_name || '-')}</td>
              <td class="px-4 py-4 text-slate-600">${escapeHtml(formatDateTime(item.starts_at))}</td>
              <td class="px-4 py-4 text-slate-600">${escapeHtml(item.location_name || '-')}</td>
              <td class="px-4 py-4"><span class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold ${statusBadgeClass(item.status)}"><span class="size-1.5 rounded-full bg-current opacity-70"></span>${statusLabel(item.status)}</span></td>
              <td class="px-4 py-4 font-semibold text-slate-900">${item.rsvp_going_count}/${item.capacity ?? rsvpTotal}</td>
              <td class="px-4 py-4">
                <div class="flex items-center justify-end gap-1 text-slate-400">
                  <button data-event-action="view" data-event-id="${item.id}" class="p-1 hover:text-primary" type="button"><span class="material-symbols-outlined text-[20px] pointer-events-none">visibility</span></button>
                  <button data-event-action="edit" data-event-id="${item.id}" class="p-1 hover:text-primary" type="button"><span class="material-symbols-outlined text-[20px] pointer-events-none">edit</span></button>
                  <button data-event-action="delete" data-event-id="${item.id}" class="p-1 hover:text-rose-500" type="button"><span class="material-symbols-outlined text-[20px] pointer-events-none">delete</span></button>
                </div>
              </td>
            </tr>
          `;
        })
        .join('');
    };

    const loadCounters = async () => {
      try {
        const nowIso = new Date().toISOString();
        const [upcoming, pending, published, cancelled, finished] = await Promise.all([
          listEvents({ status: 'published', starts_from: nowIso }),
          listEvents({ status: 'pending_review' }),
          listEvents({ status: 'published' }),
          listEvents({ status: 'cancelled' }),
          listEvents({ status: 'finished' }),
        ]);
        if (disposed) return;
        upcomingKpi.textContent = String(upcoming.count);
        pendingKpi.textContent = String(pending.count);
        publishedKpi.textContent = String(published.count);
        finishedKpi.textContent = String(cancelled.count + finished.count);
      } catch {
        if (disposed) return;
        upcomingKpi.textContent = '-';
        pendingKpi.textContent = '-';
        publishedKpi.textContent = '-';
        finishedKpi.textContent = '-';
      }
    };

    const loadEventTypes = async () => {
      try {
        const response = await listEventTypes({ ordering: 'sort_order,name', is_active: true });
        if (disposed) return;
        const current = typeFilter.value;
        typeFilter.innerHTML = '<option value="all">Type: All</option>';
        for (const type of response.results) {
          typeFilter.insertAdjacentHTML('beforeend', `<option value="${type.id}">${escapeHtml(type.name)}</option>`);
        }
        if (current && current !== 'all') typeFilter.value = current;
      } catch {
        // no-op
      }
    };

    const loadTable = async (page = currentPage) => {
      currentPage = Math.max(1, page);
      tbody.innerHTML = '<tr><td class="px-4 py-6 text-slate-500 text-sm" colspan="9">Loading events...</td></tr>';
      try {
        const params: Record<string, string | number> = {
          ordering: orderingFilter.value || '-starts_at',
          page: currentPage,
          page_size: pageSize,
        };
        const search = searchInput.value.trim();
        if (search) params.search = search;
        if (originFilter.value !== 'all') params.origin_type = originFilter.value;
        if (statusFilter.value !== 'all') params.status = statusFilter.value;
        if (typeFilter.value !== 'all') params.event_type = typeFilter.value;

        const response = await listEvents(params);
        if (disposed) return;

        renderRows(response.results);
        const from = response.count === 0 ? 0 : (currentPage - 1) * pageSize + 1;
        const to = response.count === 0 ? 0 : Math.min(from + response.results.length - 1, response.count);
        meta.textContent = `Showing ${from}-${to} of ${response.count} events`;
        pageIndicator.textContent = `Page ${currentPage}`;
        prevBtn.disabled = !response.previous;
        nextBtn.disabled = !response.next;
      } catch (error) {
        if (disposed) return;
        const message = getApiErrorMessage(error, 'Failed to load events.');
        tbody.innerHTML = `<tr><td class="px-4 py-6 text-rose-600 text-sm" colspan="9">${escapeHtml(message)}</td></tr>`;
        meta.textContent = 'Showing 0 events';
        pageIndicator.textContent = `Page ${currentPage}`;
        prevBtn.disabled = true;
        nextBtn.disabled = true;
      }
    };

    const renderView = async () => {
      if (!openedEventId) return;
      const eventId = openedEventId;
      const requestId = ++viewRequestId;
      try {
        hideViewError();
        await ensureGeoLoaded();
        const [event, mediaPayload, messagesPayload] = await Promise.all([
          getEvent(eventId),
          listAllEventMedia(eventId),
          listAllEventMessages(eventId),
        ]);
        if (disposed || requestId !== viewRequestId || openedEventId !== eventId) return;

        const orderedMedia = mediaPayload.results
          .filter((row) => !isContentBlockMediaTitle(row.title))
          .slice()
          .sort((a, b) => a.sort_order - b.sort_order);
        const hero =
          event.cover_image ||
          orderedMedia[0]?.file_url ||
          "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='675'><rect width='100%' height='100%' fill='%23e2e8f0'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%2364758b' font-size='30' font-family='Arial'>No cover</text></svg>";
        viewHero.src = hero;

        viewGallery.innerHTML = orderedMedia
          .slice(0, 8)
          .map(
            (row) => `
            <div class="aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
              <img class="w-full h-full object-cover" src="${row.file_url}" alt="Event media ${row.id}"/>
            </div>
          `
          )
          .join('');

        viewStatus.className = `px-2.5 py-1 rounded-lg text-xs font-bold uppercase ${statusBadgeClass(event.status)}`;
        viewStatus.textContent = statusLabel(event.status);
        viewOrigin.textContent = event.origin_type;
        viewFeatured.textContent = event.is_featured ? 'Featured' : 'Not Featured';
        viewFeatured.className = event.is_featured
          ? 'px-2.5 py-1 rounded-lg text-xs font-bold uppercase bg-amber-100 text-amber-700'
          : 'px-2.5 py-1 rounded-lg text-xs font-bold uppercase bg-slate-100 text-slate-700';

        viewTitle.textContent = event.title;
        viewSubtitle.textContent = `ID: #EVT-${event.id}`;
        viewType.textContent = event.event_type_name || '-';
        viewSlug.textContent = event.slug || '-';
        viewStarts.textContent = formatOptionalDateTime(event.starts_at);
        viewEnds.textContent = formatOptionalDateTime(event.ends_at);
        viewTimezone.textContent = event.timezone || '-';
        viewHost.textContent = event.host_name || '-';
        viewLocation.textContent = event.location_name || '-';
        viewCoordinates.textContent = event.latitude && event.longitude ? `${event.latitude}, ${event.longitude}` : '-';
        viewRegion.textContent = [
          event.country ? countriesById.get(event.country) : '',
          event.region ? regionsById.get(event.region) : '',
        ]
          .filter(Boolean)
          .join(', ') || '-';
        viewCounty.textContent = (event.county ? countiesById.get(event.county) : '') || '-';
        viewSettlement.textContent = (event.settlement ? settlementsById.get(event.settlement) : '') || '-';
        viewGoing.textContent = String(event.rsvp_going_count);
        viewInterested.textContent = String(event.rsvp_interested_count);
        viewNotGoing.textContent = String(event.rsvp_not_going_count);
        viewCapacity.textContent = event.capacity ? String(event.capacity) : 'No limit';
        viewPrice.textContent = event.price_from ? `$${event.price_from}` : '-';
        viewFree.textContent = event.is_free ? 'Yes' : 'No';
        viewMeetingNote.textContent = event.meeting_point_note || '-';
        const renderedBlocks = renderContentBlocksHtml(event.content_blocks);
        if (renderedBlocks) {
          viewDescription.innerHTML = renderedBlocks;
        } else {
          viewDescription.textContent = event.description || extractTextFromContentBlocks(event.content_blocks) || '-';
        }
        viewModeration.textContent = event.moderation_comment || '-';
        viewPublished.textContent = formatOptionalDateTime(event.published_at);
        viewCancelled.textContent = formatOptionalDateTime(event.cancelled_at);
        viewFinished.textContent = formatOptionalDateTime(event.finished_at);
        viewCreated.textContent = formatOptionalDateTime(event.created_at);
        viewUpdated.textContent = formatOptionalDateTime(event.updated_at);

        viewMessagesCount.textContent = String(messagesPayload.count);
        viewMessages.innerHTML = messagesPayload.results.length
          ? messagesPayload.results
              .slice(0, 20)
              .map(
                (row) => `
            <div class="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <div class="flex justify-between items-center mb-1">
                <span class="text-xs font-semibold text-slate-800">${escapeHtml(row.author_name)}</span>
                <span class="text-[11px] text-slate-500">${escapeHtml(formatDateTime(row.created_at))}</span>
              </div>
              <p class="text-sm text-slate-700">${escapeHtml(row.message)}</p>
            </div>
          `
              )
              .join('')
          : '<p class="text-sm text-slate-500">No messages yet.</p>';

        viewPublishBtn.disabled = event.status === 'published';
      } catch (error) {
        if (disposed || requestId !== viewRequestId || openedEventId !== eventId) return;
        showViewError(getApiErrorMessage(error, 'Failed to load event details.'));
      }
    };

    const openView = async (id: number) => {
      openedEventId = id;
      viewOverlay.classList.remove('hidden');
      updateViewQueryParam(String(id));
      setViewLoadingState(id);
      await renderView();
      await loadTable();
    };

    const sendMessage = async () => {
      const text = viewMessageInput.value.trim();
      if (!openedEventId || !text) return;
      try {
        await createEventMessage(openedEventId, text);
        viewMessageInput.value = '';
        await renderView();
      } catch (error) {
        showViewError(getApiErrorMessage(error, 'Failed to send message.'));
      }
    };

    const onSearchInput = () => {
      if (searchTimer) window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => {
        currentPage = 1;
        void loadTable(1);
      }, 300);
    };

    const onFilterChange = () => {
      currentPage = 1;
      void loadTable(1);
    };

    const onResetFilters = () => {
      searchInput.value = '';
      originFilter.value = 'all';
      statusFilter.value = 'all';
      typeFilter.value = 'all';
      orderingFilter.value = 'starts_at';
      currentPage = 1;
      void loadTable(1);
    };

    const onTableClick = async (event: Event) => {
      const target = event.target as HTMLElement;
      const button = target.closest<HTMLButtonElement>('button[data-event-action][data-event-id]');
      if (!button) return;
      const id = Number(button.dataset.eventId || '');
      if (!id) return;
      const action = button.dataset.eventAction;

      if (action === 'view') {
        await openView(id);
        return;
      }

      if (action === 'edit') {
        navigate(`/events/create?id=${id}`);
        return;
      }

      if (action === 'delete') {
        if (!window.confirm('Delete event?')) return;
        try {
          await deleteEvent(id);
          if (openedEventId === id) closeView();
          await Promise.all([loadTable(), loadCounters()]);
        } catch (error) {
          window.alert(getApiErrorMessage(error, 'Failed to delete event.'));
        }
      }
    };

    const onCreateClickCapture = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const createAnchor = target.closest<HTMLAnchorElement>('a[href="/events/create"]');
      if (!createAnchor) return;
      event.preventDefault();
      navigate('/events/create');
    };

    const onOverlayClose = () => closeView();
    const onPrevPage = () => {
      if (currentPage <= 1) return;
      void loadTable(currentPage - 1);
    };
    const onNextPage = () => {
      void loadTable(currentPage + 1);
    };
    const onMessageSendClick = () => {
      void sendMessage();
    };
    const onMessageKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        void sendMessage();
      }
    };

    const onViewEdit = () => {
      if (!openedEventId) return;
      navigate(`/events/create?id=${openedEventId}`);
    };

    const onViewPublish = async () => {
      if (!openedEventId) return;
      try {
        await publishEvent(openedEventId);
        await Promise.all([renderView(), loadTable(), loadCounters()]);
      } catch (error) {
        showViewError(getApiErrorMessage(error, 'Failed to publish event.'));
      }
    };

    const onViewCancel = async () => {
      if (!openedEventId) return;
      if (!window.confirm('Cancel this event?')) return;
      try {
        await cancelEvent(openedEventId);
        await Promise.all([renderView(), loadTable(), loadCounters()]);
      } catch (error) {
        showViewError(getApiErrorMessage(error, 'Failed to cancel event.'));
      }
    };

    const onViewFinish = async () => {
      if (!openedEventId) return;
      if (!window.confirm('Finish this event?')) return;
      try {
        await finishEvent(openedEventId);
        await Promise.all([renderView(), loadTable(), loadCounters()]);
      } catch (error) {
        showViewError(getApiErrorMessage(error, 'Failed to finish event.'));
      }
    };

    const onViewDelete = async () => {
      if (!openedEventId) return;
      if (!window.confirm('Delete this event?')) return;
      try {
        await deleteEvent(openedEventId);
        closeView();
        await Promise.all([loadTable(), loadCounters()]);
      } catch (error) {
        showViewError(getApiErrorMessage(error, 'Failed to delete event.'));
      }
    };

    searchInput.addEventListener('input', onSearchInput);
    originFilter.addEventListener('change', onFilterChange);
    statusFilter.addEventListener('change', onFilterChange);
    typeFilter.addEventListener('change', onFilterChange);
    orderingFilter.addEventListener('change', onFilterChange);
    resetFiltersBtn.addEventListener('click', onResetFilters);
    tbody.addEventListener('click', onTableClick);
    root.addEventListener('click', onCreateClickCapture, true);
    prevBtn.addEventListener('click', onPrevPage);
    nextBtn.addEventListener('click', onNextPage);

    viewCloseBtn.addEventListener('click', onOverlayClose);
    viewBackdrop.addEventListener('click', onOverlayClose);
    viewMessageSend.addEventListener('click', onMessageSendClick);
    viewMessageInput.addEventListener('keydown', onMessageKeyDown);
    viewEditBtn.addEventListener('click', onViewEdit);
    viewPublishBtn.addEventListener('click', () => {
      void onViewPublish();
    });
    viewCancelBtn.addEventListener('click', () => {
      void onViewCancel();
    });
    viewFinishBtn.addEventListener('click', () => {
      void onViewFinish();
    });
    viewDeleteBtn.addEventListener('click', () => {
      void onViewDelete();
    });

    void (async () => {
      await Promise.all([loadEventTypes(), loadTable(), loadCounters()]);
      const initialView = new URLSearchParams(window.location.search).get('view');
      if (initialView && /^\d+$/.test(initialView)) {
        await openView(Number(initialView));
      }
    })();

    return () => {
      disposed = true;
      if (searchTimer) window.clearTimeout(searchTimer);
      searchInput.removeEventListener('input', onSearchInput);
      originFilter.removeEventListener('change', onFilterChange);
      statusFilter.removeEventListener('change', onFilterChange);
      typeFilter.removeEventListener('change', onFilterChange);
      orderingFilter.removeEventListener('change', onFilterChange);
      resetFiltersBtn.removeEventListener('click', onResetFilters);
      tbody.removeEventListener('click', onTableClick);
      root.removeEventListener('click', onCreateClickCapture, true);
      prevBtn.removeEventListener('click', onPrevPage);
      nextBtn.removeEventListener('click', onNextPage);
      viewCloseBtn.removeEventListener('click', onOverlayClose);
      viewBackdrop.removeEventListener('click', onOverlayClose);
      viewMessageSend.removeEventListener('click', onMessageSendClick);
      viewMessageInput.removeEventListener('keydown', onMessageKeyDown);
      viewEditBtn.removeEventListener('click', onViewEdit);
    };
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>MotoJourney | Events</title>
      </Helmet>
      <div className="font-display bg-background-light text-slate-900 min-h-screen">
        <div ref={containerRef} dangerouslySetInnerHTML={{ __html: template }} />
      </div>
    </>
  );
}
